import CalendarIllustration from "@/components/ui/CalendarIllustration.tsx";
import routes from "@/config/routes.ts";
import { useBookDiplomaRequestMutation } from "@/data/diploma.ts";
import { useMe } from "@/data/user.ts";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
import useDiplomaRequest from "@/utils/hooks/useDiplomaRequest.ts";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import Button from "@edusogno/primitives/button";
import LoadingButton from "@edusogno/primitives/loading-button";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const DiplomaRequestConfirm = () => {
  const { calendar, hour, requestedAlready, date } = useDiplomaRequest();
  const newDate = dayjs(date);

  const { setGlobalState } = useGlobals();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { me } = useMe();
  const { mutate, isLoading } = useBookDiplomaRequestMutation();

  const handleConfirm = () => {
    if (!me || !calendar) return;

    mutate({
      start: dayjs(calendar.date).format("YYYY-MM-DD HH:mm"),
      summary: me.item.user.first_name + " " + me.item.user.last_name,
      calendar: [calendar.calendar],
      attendees: [me.item.user.email],
      newStatus: "Upselling scheduled",
      user_token: me.item.user.token,
      isUpselling: true,
      calendar_id: calendar.calendar_id,
    });
  };

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-light");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!date) {
      navigate(routes.diploma_request_select_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <DashboardContentLayout>
      <div className="mx-auto max-w-md w-full min-h-full h-full flex flex-col">
        <header className="flex flex-col items-center">
          {requestedAlready ? (
            <p className="text-brand-secondary text-lg font-semibold text-start">
              {t("diploma_request.modal.already_requested")}
            </p>
          ) : (
            <div className="space-y-1.5">
              <p className="!text-secondary text-2xl font-semibold">
                {t("diploma_request.modal.confirm_message")}
              </p>

              <p className="text-14px text-primary">
                {t("diploma_request.modal.calendar_invite")}&nbsp;
                <b className="text-brand">
                  {t("diploma_request.modal.email_address")}
                </b>
                &nbsp;
                {t("diploma_request.modal.calendar_invite_end")}
              </p>
            </div>
          )}
        </header>

        <div className="flex flex-col items-center justify-start mt-4 h-full w-full">
          <CalendarIllustration
            day={newDate.format("DD")}
            month={newDate.format("MMM")}
            header={newDate.format("ddd")}
            className="w-2/4 border-solid border-2 rounded-md block text-center translate-x-1"
          />

          <div className="flex flex-col gap-8 h-fit my-auto items-center">
            <div className="mt-4 text-center">
              <h5 className="text-brand text-15px">
                {t("diploma_request.modal.time")}
              </h5>

              <p className="text-brand-secondary font-semibold text-2xl">
                {hour}
              </p>
            </div>

            <div className="mt-4 text-center">
              <h5 className="text-brand text-15px">
                {t("diploma_request.modal.duration")}
              </h5>

              <p className="text-brand-secondary font-semibold text-2xl">
                {20}&nbsp;{t("diploma_request.modal.minutes")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex mt-6 w-full justify-between flex-1 flex-grow gap-x-2 px-6">
          <Button size="md" variant="contained" onClick={() => navigate(-1)}>
            {t("btns.back")}
          </Button>

          <LoadingButton
            size="md"
            color="green"
            variant="contained"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading || requestedAlready}
            className="flex gap-1 justify-center items-center"
          >
            {t("btns.confirm")}
          </LoadingButton>
        </div>
      </div>
    </DashboardContentLayout>
  );
};

export default DiplomaRequestConfirm;
